/**
 * "Publish to production" for a forge: what will be published (the arche
 * settings, edited in their own dialog), the forge's "copy test data" choice,
 * and Publish, which queues the publish through the normal pipeline.
 *
 * What the dialog shows is what is published: settings the owner never saved
 * (the arche's current details) are saved first, so they are laid over the
 * workspace manifest like saved ones rather than leaving its placeholder name
 * and description in charge.
 */
import { useEffect, useState } from 'react';
import { ImageOff, Loader2, Rocket, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useForgePublishSettings } from '../hooks/useForgePublishSettings';
import {
  archeSettingsComplete,
  describeArcheSettings,
  upcomingArcheIcon,
} from './archeSettingsSummary';

export interface ForgePublishDialogProps {
  forgeId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Queue the publish; rejects with the server's message. */
  onPublish: (options: { carryTestData: boolean }) => Promise<void>;
  /** The forge must be running (and between turns) to publish. */
  canPublish: boolean;
  /** Open the arche settings dialog (this one closes). */
  onEditSettings: () => void;
}

export function ForgePublishDialog({
  forgeId,
  open,
  onOpenChange,
  onPublish,
  canPublish,
  onEditSettings,
}: ForgePublishDialogProps) {
  const { view, isLoading, error: loadError, save } = useForgePublishSettings(forgeId, open);
  const [carryTestData, setCarryTestData] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setCarryTestData(false);
      setError(null);
    }
  }, [open]);

  const complete = view ? archeSettingsComplete(view.settings) : false;

  const publish = async () => {
    if (!view || !complete) return;
    setBusy(true);
    setError(null);
    try {
      if (!view.saved) await save(view.settings);
      await onPublish({ carryTestData });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const icon = view ? upcomingArcheIcon(view) : null;

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent className="sm:max-w-[480px]" data-testid="forge-publish-dialog">
        <DialogHeader>
          <DialogTitle>Publish to production</DialogTitle>
          <DialogDescription>
            Publishing sends the arche through the usual security review and build before it goes
            live.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {!view ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loadError ?? 'Loading the arche settings…'}
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="rounded-lg border p-3" data-testid="forge-publish-summary">
                <div className="flex items-start gap-3">
                  <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md border bg-muted">
                    {icon ? (
                      <img src={icon} alt="Arche icon" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                        <ImageOff className="h-4 w-4" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {view.settings.name || 'No name yet'}
                    </p>
                    <p className="line-clamp-2 text-xs text-muted-foreground">
                      {view.settings.description || 'No description yet'}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {describeArcheSettings(view.settings).join(' · ')}
                    </p>
                  </div>
                </div>
                <Button
                  variant="link"
                  size="sm"
                  className="mt-1 h-auto px-0"
                  onClick={onEditSettings}
                  disabled={busy}
                >
                  <Settings className="h-3.5 w-3.5 mr-1" />
                  Edit arche settings
                </Button>
              </div>
              {!complete && (
                <p role="alert" className="text-sm text-destructive">
                  Add a name and a short description in the arche settings before publishing.
                </p>
              )}
              <label className="flex items-start gap-2 text-sm">
                <Checkbox
                  checked={carryTestData}
                  onCheckedChange={(v) => setCarryTestData(v === true)}
                  aria-label="Copy test data to production"
                />
                <span>
                  Copy test data to production
                  <span className="block text-xs text-muted-foreground">
                    Seeds the live arche with the test version&apos;s data. Only an arche with no
                    data yet is seeded.
                  </span>
                </span>
              </label>
              {!canPublish && (
                <p className="text-xs text-muted-foreground">
                  You can publish while the forge is running and Claude Code is between turns.
                </p>
              )}
              {error && (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={() => void publish()}
            disabled={!complete || busy || !canPublish}
            title={canPublish ? undefined : 'Start the forge to publish'}
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
            ) : (
              <Rocket className="h-4 w-4 mr-1.5" />
            )}
            Publish
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
